import { useEffect } from "react";
import { optionOn, optionRule, variantAxes } from "@/lib/signs";
import { ChoiceField, SizeField, ToggleField } from "@/components/signs/Fields";

/** Old true/false values and blanks, read as the choice they stand for. */
const normalize = (rule, v) => {
    if (v === true) return "yes";
    if (v === false || v == null || v === "") return rule.choices.find((c) => c.id === "no" || c.id === "none")?.id || rule.choices[0].id;
    return v;
};

const SIDES_LABEL = { single: "Single-sided", double: "Double-sided" };
const perSqft = (v) => (v?.price_per_sqft ? ` · $${v.price_per_sqft}/sqft` : "");

/** Material and print sides, split into two fields when the variants carry both. */
function VariantFields({ product, cfg, theme }) {
    const variants = product.variants || [];
    const axes = variantAxes(variants);
    const variant = variants.find((v) => v.id === cfg.variantId);
    if (variants.length < 2) return null;

    if (!axes.split) {
        return (
            <ChoiceField label="Material" theme={theme} testid="field-material" value={cfg.variantId} onChange={cfg.setVariantId}
                choices={variants.map((v) => ({ id: v.id, label: `${v.label}${perSqft(v)}` }))} />
        );
    }
    const pickMaterial = (m) => {
        const v = variants.find((x) => x.material === m && x.print_sides === variant?.print_sides) || variants.find((x) => x.material === m);
        cfg.setVariantId(v.id);
    };
    const pickSides = (s) => {
        const v = variants.find((x) => x.material === variant?.material && x.print_sides === s);
        if (v) cfg.setVariantId(v.id);
    };
    const available = axes.sidesFor(variant?.material);
    return (
        <>
            <ChoiceField label="Material" theme={theme} testid="field-material" value={variant?.material} onChange={pickMaterial}
                choices={axes.materials.map((m) => {
                    const v = variants.find((x) => x.material === m && x.print_sides === variant?.print_sides) || variants.find((x) => x.material === m);
                    return { id: m, label: `${m}${perSqft(v)}` };
                })} />
            <ChoiceField label="Print sides" theme={theme} testid="field-print-sides" value={variant?.print_sides} onChange={pickSides}
                choices={["single", "double"].map((s) => {
                    const v = variants.find((x) => x.material === variant?.material && x.print_sides === s);
                    return { id: s, label: `${SIDES_LABEL[s]}${perSqft(v)}`, disabled: !available.includes(s), note: `not on ${variant?.material}` };
                })} />
        </>
    );
}

/** The row of white fields along the bottom: size, material, then every finishing option. */
export default function OptionDock({ product, cfg, theme }) {
    const opts = product.options || [];
    const ctx = { variant: (product.variants || []).find((v) => v.id === cfg.variantId) };

    // an option that just became off-limits switches itself off; a choice that stopped making sense picks its fallback
    useEffect(() => {
        opts.forEach((opt) => {
            const rule = optionRule(opt);
            const cur = cfg.options[opt.id];
            if (rule.blockedBy(cfg.options, ctx) && optionOn(cur)) return cfg.setOption(opt.id, normalize(rule, false));
            const fixed = rule.fixValue(cur, cfg.options, ctx);
            if (fixed !== cur) cfg.setOption(opt.id, fixed);
        });
    }, [cfg.options, cfg.variantId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="border-t border-zinc-200 bg-[#f7f7f8] px-4 py-3 sm:px-6" data-testid="option-dock">
            <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-wrap lg:justify-center lg:overflow-visible lg:pb-0">
                <SizeField product={product} cfg={cfg} theme={theme} />
                <VariantFields product={product} cfg={cfg} theme={theme} />
                {opts.map((opt) => {
                    const rule = optionRule(opt);
                    const choices = rule.choices.map((c) => {
                        const note = rule.disabledChoice(c.id, cfg.options, ctx);
                        return note ? { ...c, disabled: true, note } : c;
                    });
                    const props = {
                        label: opt.label, choices, value: normalize(rule, cfg.options[opt.id]), theme,
                        onChange: (v) => cfg.setOption(opt.id, v), blocked: rule.blockedBy(cfg.options, ctx), help: rule.help, testid: `field-${opt.id}`,
                    };
                    return rule.choices.length === 2 ? <ToggleField key={opt.id} {...props} /> : <ChoiceField key={opt.id} {...props} />;
                })}
            </div>
        </div>
    );
}
